import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { MessageSquare, Smile, Meh, Frown, Hash, Info, FileText, BookOpen, MessagesSquare, Layers, Eye } from 'lucide-react';
import { useWizard } from '../../../context/WizardContext';
import ContentAnalysis from './ContentAnalysis';
import NarrativeAnalysis from './NarrativeAnalysis';
import DiscourseAnalysis from './DiscourseAnalysis';
import GroundedTheory from './GroundedTheory';
import PhenomenologicalAnalysis from './PhenomenologicalAnalysis';
import { positiveWords, negativeWords, subjectiveWords, stopWords } from './qualitativeUtils';
import './TextAnalysis.css';

const COLORS = ['#22c55e', '#f59e0b', '#ef4444'];

const analysisMethods = [
  { id: 'sentiment', name: 'Sentiment', icon: Smile, methodKey: 'sentimentAnalysis' },
  { id: 'thematic', name: 'Thematic', icon: Hash, methodKey: 'thematicAnalysis' },
  { id: 'content', name: 'Content', icon: FileText, methodKey: 'contentAnalysis' },
  { id: 'narrative', name: 'Narrative', icon: BookOpen, methodKey: 'narrativeAnalysis' },
  { id: 'discourse', name: 'Discourse', icon: MessagesSquare, methodKey: 'discourseAnalysis' },
  { id: 'grounded', name: 'Grounded Theory', icon: Layers, methodKey: 'groundedTheory' },
  { id: 'phenomenological', name: 'Phenomenological', icon: Eye, methodKey: 'phenomenologicalAnalysis' }
];

const TextAnalysis = ({ survey }) => {
  const { dataB } = useWizard();
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [activeMethod, setActiveMethod] = useState('sentiment');

  const responses = survey.responses || [];
  const questions = survey.questions || [];

  const plannedMethods = useMemo(() => {
    const methods = dataB?.qualitativeMethods || {};
    return Object.entries(methods)
      .filter(([_, enabled]) => enabled)
      .map(([key]) => key);
  }, [dataB]);

  const textQuestions = useMemo(() => 
    questions.filter(q => ['shortAnswer', 'longAnswer'].includes(q.type)),
    [questions]
  );

  const getTextResponses = (questionId) => {
    return responses
      .map(r => r.answers?.[questionId])
      .filter(a => a && typeof a === 'string' && a.trim().length > 0);
  };

  const tokenize = (text) => {
    return text.toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2 && !stopWords.has(word));
  };

  const analyzeSentiment = (texts) => {
    const results = texts.map(text => {
      const words = tokenize(text);
      let positiveCount = 0;
      let negativeCount = 0;
      let subjectiveCount = 0;

      words.forEach(word => {
        if (positiveWords.has(word)) positiveCount++;
        if (negativeWords.has(word)) negativeCount++;
        if (subjectiveWords.has(word)) subjectiveCount++;
      });

      const total = words.length || 1;
      const polarity = (positiveCount - negativeCount) / Math.max(positiveCount + negativeCount, 1);
      const subjectivity = subjectiveCount / total;

      let sentiment = 'neutral';
      if (polarity > 0.1) sentiment = 'positive';
      else if (polarity < -0.1) sentiment = 'negative';

      return {
        text,
        sentiment,
        polarity: Math.round(polarity * 100) / 100,
        subjectivity: Math.round(subjectivity * 100) / 100,
        positiveCount,
        negativeCount,
        wordCount: words.length
      };
    });

    const sentimentCounts = { positive: 0, neutral: 0, negative: 0 };
    let totalPolarity = 0;
    let totalSubjectivity = 0;

    results.forEach(r => {
      sentimentCounts[r.sentiment]++;
      totalPolarity += r.polarity;
      totalSubjectivity += r.subjectivity;
    });

    const avgPolarity = results.length > 0 ? totalPolarity / results.length : 0;
    const avgSubjectivity = results.length > 0 ? totalSubjectivity / results.length : 0;

    return {
      individual: results,
      summary: {
        positive: sentimentCounts.positive,
        neutral: sentimentCounts.neutral,
        negative: sentimentCounts.negative,
        avgPolarity: Math.round(avgPolarity * 100) / 100,
        avgSubjectivity: Math.round(avgSubjectivity * 100) / 100,
        total: results.length
      }
    };
  };

  const analyzeThemes = (texts) => {
    const wordFreq = {};
    const bigramFreq = {};

    texts.forEach(text => {
      const words = tokenize(text);
      
      words.forEach(word => {
        wordFreq[word] = (wordFreq[word] || 0) + 1;
      });

      for (let i = 0; i < words.length - 1; i++) {
        const bigram = `${words[i]} ${words[i + 1]}`;
        bigramFreq[bigram] = (bigramFreq[bigram] || 0) + 1;
      }
    });

    const topWords = Object.entries(wordFreq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .map(([word, count]) => ({ word, count }));

    const topBigrams = Object.entries(bigramFreq)
      .filter(([_, count]) => count >= 2)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([phrase, count]) => ({ phrase, count }));

    const themes = identifyThemes(topWords);

    return {
      topWords,
      topBigrams,
      themes,
      totalWords: Object.values(wordFreq).reduce((a, b) => a + b, 0),
      uniqueWords: Object.keys(wordFreq).length
    };
  };

  const identifyThemes = (topWords) => {
    const themeGroups = [
      { name: 'Quality & Performance', keywords: ['quality', 'performance', 'reliable', 'efficient', 'fast', 'slow', 'works', 'broken'] },
      { name: 'Customer Service', keywords: ['service', 'staff', 'support', 'help', 'friendly', 'rude', 'response', 'team'] },
      { name: 'Value & Pricing', keywords: ['price', 'value', 'cost', 'expensive', 'cheap', 'worth', 'money', 'affordable'] },
      { name: 'User Experience', keywords: ['easy', 'simple', 'difficult', 'confusing', 'intuitive', 'interface', 'design', 'use'] },
      { name: 'Satisfaction', keywords: ['happy', 'satisfied', 'disappointed', 'recommend', 'love', 'hate', 'great', 'terrible'] }
    ];

    const wordSet = new Set(topWords.map(w => w.word));
    
    return themeGroups
      .map(theme => {
        const matchingWords = theme.keywords.filter(k => wordSet.has(k));
        const relevance = matchingWords.length / theme.keywords.length;
        const mentions = matchingWords.reduce((sum, word) => {
          const found = topWords.find(w => w.word === word);
          return sum + (found?.count || 0);
        }, 0);
        
        return {
          name: theme.name,
          relevance: Math.round(relevance * 100),
          mentions,
          keywords: matchingWords
        };
      })
      .filter(t => t.relevance > 0)
      .sort((a, b) => b.relevance - a.relevance);
  };

  const generateSentimentExplanation = (sentiment) => {
    const { summary } = sentiment;
    const total = summary.total;
    const posPercent = Math.round((summary.positive / total) * 100);
    const negPercent = Math.round((summary.negative / total) * 100);
    const neutralPercent = Math.round((summary.neutral / total) * 100);

    let explanation = `Sentiment analysis was performed on ${total} text responses using a lexicon-based approach. `;
    explanation += `The analysis identified ${summary.positive} positive (${posPercent}%), ${summary.neutral} neutral (${neutralPercent}%), and ${summary.negative} negative (${negPercent}%) responses. `;
    explanation += `\n\nThe average polarity score was ${summary.avgPolarity} on a scale from -1 (very negative) to +1 (very positive). `;
    
    if (summary.avgPolarity > 0.2) {
      explanation += `This indicates an overall positive sentiment in the responses. `;
    } else if (summary.avgPolarity < -0.2) {
      explanation += `This indicates an overall negative sentiment that may require attention. `;
    } else {
      explanation += `This suggests a generally neutral or mixed sentiment across responses. `;
    }

    explanation += `\n\nThe average subjectivity score was ${summary.avgSubjectivity} on a scale from 0 (objective) to 1 (subjective). `;
    
    if (summary.avgSubjectivity > 0.5) {
      explanation += `Responses tend to be more subjective, expressing personal opinions and feelings rather than factual statements. `;
    } else {
      explanation += `Responses lean toward objectivity, containing more factual descriptions than emotional expressions. `;
    }

    explanation += `\n\nNote: This analysis uses a rule-based approach with predefined word lists. For more nuanced analysis, consider qualitative coding methods.`;

    return explanation;
  };

  const generateThemeExplanation = (themes) => {
    let explanation = `Thematic analysis was conducted using word frequency and co-occurrence patterns. `;
    explanation += `A total of ${themes.totalWords} words were analyzed, with ${themes.uniqueWords} unique terms identified. `;
    
    if (themes.themes.length > 0) {
      explanation += `\n\nThe following themes emerged from the analysis:\n`;
      themes.themes.forEach((theme, index) => {
        explanation += `\n${index + 1}. **${theme.name}** (${theme.relevance}% relevance): Mentioned ${theme.mentions} times through keywords like "${theme.keywords.join('", "')}". `;
      });
    }

    explanation += `\n\nThe most frequently occurring words provide insight into common topics and concerns. `;
    if (themes.topWords.length > 0) {
      explanation += `The top 5 words were: ${themes.topWords.slice(0, 5).map(w => `"${w.word}" (${w.count})`).join(', ')}. `;
    }

    if (themes.topBigrams.length > 0) {
      explanation += `\n\nCommon phrases (bigrams) can reveal more specific topics. Notable phrases include: ${themes.topBigrams.slice(0, 3).map(b => `"${b.phrase}" (${b.count})`).join(', ')}. `;
    }

    explanation += `\n\nThis automated thematic analysis provides a starting point for understanding response patterns. For deeper insights, manual qualitative coding is recommended.`;

    return explanation;
  };

  const currentTextResponses = selectedQuestion ? getTextResponses(selectedQuestion) : [];
  const sentimentResults = currentTextResponses.length > 0 ? analyzeSentiment(currentTextResponses) : null;
  const themeResults = currentTextResponses.length > 0 ? analyzeThemes(currentTextResponses) : null;

  if (textQuestions.length === 0) {
    return (
      <div className="text-analysis-empty">
        <MessageSquare size={48} />
        <h3>No Text Questions</h3>
        <p>Add short answer or long answer questions to your survey to use text analysis.</p>
      </div>
    );
  }

  const renderAnalysisContent = () => {
    if (!selectedQuestion) {
      return (
        <div className="no-responses">
          <p>Select a text question above to begin analysis.</p>
        </div>
      );
    }

    if (currentTextResponses.length === 0) {
      return (
        <div className="no-responses">
          <p>No text responses collected for this question yet.</p>
        </div>
      );
    }

    switch (activeMethod) {
      case 'sentiment':
        return sentimentResults && (
          <div className="analysis-section sentiment-section">
            <h4><Smile size={20} /> Sentiment Analysis</h4>
            
            <div className="sentiment-summary">
              <div className="sentiment-card positive">
                <Smile size={24} />
                <div className="sentiment-value">{sentimentResults.summary.positive}</div>
                <div className="sentiment-label">Positive</div>
              </div>
              <div className="sentiment-card neutral">
                <Meh size={24} />
                <div className="sentiment-value">{sentimentResults.summary.neutral}</div>
                <div className="sentiment-label">Neutral</div>
              </div>
              <div className="sentiment-card negative">
                <Frown size={24} />
                <div className="sentiment-value">{sentimentResults.summary.negative}</div>
                <div className="sentiment-label">Negative</div>
              </div>
            </div>

            <div className="metrics-row">
              <div className="metric">
                <div className="metric-label">Average Polarity</div>
                <div className={`metric-value ${sentimentResults.summary.avgPolarity > 0 ? 'positive' : sentimentResults.summary.avgPolarity < 0 ? 'negative' : ''}`}>
                  {sentimentResults.summary.avgPolarity > 0 ? '+' : ''}{sentimentResults.summary.avgPolarity}
                </div>
                <div className="metric-scale">-1 (negative) to +1 (positive)</div>
              </div>
              <div className="metric">
                <div className="metric-label">Average Subjectivity</div>
                <div className="metric-value">{sentimentResults.summary.avgSubjectivity}</div>
                <div className="metric-scale">0 (objective) to 1 (subjective)</div>
              </div>
            </div>

            <div className="chart-section">
              <h5>Sentiment Distribution</h5>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Positive', value: sentimentResults.summary.positive },
                      { name: 'Neutral', value: sentimentResults.summary.neutral },
                      { name: 'Negative', value: sentimentResults.summary.negative }
                    ]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={index} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="response-list">
              <h5>Response Details</h5>
              {sentimentResults.individual.slice(0, 10).map((item, index) => (
                <div key={index} className={`response-item ${item.sentiment}`}>
                  <div className="response-sentiment-badge">
                    {item.sentiment === 'positive' ? <Smile size={16} /> : 
                     item.sentiment === 'negative' ? <Frown size={16} /> : <Meh size={16} />}
                    {item.sentiment}
                  </div>
                  <div className="response-text">"{item.text}"</div>
                  <div className="response-stats">
                    Polarity: {item.polarity} | Subjectivity: {item.subjectivity}
                  </div>
                </div>
              ))}
              {sentimentResults.individual.length > 10 && (
                <div className="more-responses">+{sentimentResults.individual.length - 10} more responses</div>
              )}
            </div>

            <div className="analysis-explanation">
              <h5><Info size={18} /> Interpretation</h5>
              <p>{generateSentimentExplanation(sentimentResults)}</p>
            </div>
          </div>
        );

      case 'thematic':
        return themeResults && (
          <div className="analysis-section theme-section">
            <h4><Hash size={20} /> Thematic Analysis</h4>

            <div className="theme-stats">
              <div className="theme-stat">
                <div className="stat-value">{themeResults.totalWords}</div>
                <div className="stat-label">Total Words</div>
              </div>
              <div className="theme-stat">
                <div className="stat-value">{themeResults.uniqueWords}</div>
                <div className="stat-label">Unique Words</div>
              </div>
              <div className="theme-stat">
                <div className="stat-value">{themeResults.themes.length}</div>
                <div className="stat-label">Themes Identified</div>
              </div>
            </div>

            {themeResults.themes.length > 0 && (
              <div className="themes-list">
                <h5>Identified Themes</h5>
                {themeResults.themes.map((theme, index) => (
                  <div key={index} className="theme-item">
                    <div className="theme-header">
                      <span className="theme-name">{theme.name}</span>
                      <span className="theme-relevance">{theme.relevance}% relevance</span>
                    </div>
                    <div className="theme-keywords">
                      {theme.keywords.map((kw, i) => (
                        <span key={i} className="keyword-tag">{kw}</span>
                      ))}
                    </div>
                    <div className="theme-mentions">{theme.mentions} mentions</div>
                  </div>
                ))}
              </div>
            )}

            <div className="chart-section">
              <h5>Top Words</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={themeResults.topWords.slice(0, 15)} layout="vertical" margin={{ left: 80, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="word" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {themeResults.topBigrams.length > 0 && (
              <div className="bigrams-section">
                <h5>Common Phrases</h5>
                <div className="bigrams-list">
                  {themeResults.topBigrams.map((bg, index) => (
                    <div key={index} className="bigram-item">
                      <span className="bigram-phrase">"{bg.phrase}"</span>
                      <span className="bigram-count">{bg.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="analysis-explanation">
              <h5><Info size={18} /> Interpretation</h5>
              <p>{generateThemeExplanation(themeResults)}</p>
            </div>
          </div>
        );

      case 'content':
        return <ContentAnalysis texts={currentTextResponses} surveyId={survey.id} />;

      case 'narrative':
        return <NarrativeAnalysis texts={currentTextResponses} />;

      case 'discourse':
        return <DiscourseAnalysis texts={currentTextResponses} />;

      case 'grounded':
        return <GroundedTheory texts={currentTextResponses} surveyId={survey.id} />;

      case 'phenomenological':
        return <PhenomenologicalAnalysis texts={currentTextResponses} surveyId={survey.id} />;

      default:
        return null;
    }
  };

  return (
    <div className="text-analysis">
      <div className="text-analysis-header">
        <h3>Qualitative Text Analysis</h3>
        <p>Analyze open-ended responses using multiple qualitative methods</p>
      </div>

      <div className="analysis-controls">
        <div className="control-group">
          <label>Select Question:</label>
          <select 
            value={selectedQuestion || ''} 
            onChange={e => setSelectedQuestion(e.target.value)}
          >
            <option value="">Choose a text question...</option>
            {textQuestions.map(q => (
              <option key={q.id} value={q.id}>{q.text.substring(0, 60)}...</option>
            ))}
          </select>
        </div>
      </div>

      {selectedQuestion && (
        <div className="analysis-tabs">
          {analysisMethods.map(method => {
            const Icon = method.icon;
            const isPlanned = plannedMethods.includes(method.methodKey);
            return (
              <button
                key={method.id}
                className={`analysis-tab ${activeMethod === method.id ? 'active' : ''} ${isPlanned ? 'recommended' : ''}`}
                data-method={method.id}
                onClick={() => setActiveMethod(method.id)}
              >
                <Icon size={18} />
                <span>{method.name}</span>
                {isPlanned && <span className="badge">Planned</span>}
              </button>
            );
          })}
        </div>
      )}

      {renderAnalysisContent()}
    </div>
  );
};

export default TextAnalysis;
